import { useState, useRef, useMemo } from "react";
import { DialogTitle, Description } from "@headlessui/react";
import {
  FaImage,
  FaPhotoVideo,
  FaVideo,
  FaCheckCircle,
  FaExclamationCircle,
  FaSpinner,
} from "react-icons/fa";
import { FiUpload } from "react-icons/fi";
import { nConcurrent } from "@memento/library/promise";
import { debounceTrailing } from "@memento/library/fns";
import { assertNever, notEmpty } from "@memento/library/type-witchcraft";
import { type PipeCustomPayload } from "@memento/types/pipe";
import { type Card } from "@memento/types/card";
import { type GeoRegion } from "@memento/types/feature-flag";
import { Button } from "@memento/ui/Button";
import { ApplePhotosTip } from "@memento/ui/ApplePhotosTip";
import { Modal } from "src/components/Elements/Modal";
import { cn } from "src/utils/cn";
import { useAuth } from "src/hooks/useAuth";
import { GetAccessTokenFn } from "src/contexts/AuthProvider";
import { toastError } from "src/libs/toast-lib";
import config from "src/config";
import {
  getPipeVideoUploadPayload,
  GetPipeVideoUploadPayloadProps,
} from "src/libs/pipe-lib";
import { s3Upload } from "src/libs/s3-lib";
import { createPhotoSegment } from "src/hooks/useCreatePhotoSegment";
import { addPendingSegment } from "src/requests/segment";
import { defaultPhotoClipDurationSeconds } from "src/types/renderer";
import { useSendAnalyticsEvent } from "src/libs/analytics-lib";
import { useFeatureFlags } from "src/contexts/FeatureFlagProvider";

const MAX_CONCURRENT_UPLOADS = 5;
// according to the pipe API docs, the max file size is 5 GiB
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024 * 1024; // 5 GiB
const MAX_FILE_SIZE_HUMAN = "5 GB"; // a human-readable version of MAX_FILE_SIZE_BYTES

type FileUploadStatus = "pending" | "uploading" | "error" | "completed";

function FileUploadIcon({ status }: { status: FileUploadStatus }) {
  switch (status) {
    case "uploading":
      return (
        <FaSpinner className="h-4 w-4 shrink-0 animate-spin text-blue-500" />
      );
    case "pending":
      return <FaSpinner className="h-4 w-4 shrink-0 text-gray-400" />;
    case "completed":
      return <FaCheckCircle className="h-4 w-4 shrink-0 text-green-500" />;
    case "error":
      return <FaExclamationCircle className="h-4 w-4 shrink-0 text-red-500" />;
  }
}

type FileUploadItem = {
  id: string;
  file: File;
  status: FileUploadStatus;
  progress: number;
  error?: string;
};

type FileUploadItemProps = {
  item: FileUploadItem;
  onRetry: (id: string) => void;
};

function FileUploadItem({ item, onRetry }: FileUploadItemProps) {
  const isVideo = item.file.type.startsWith("video/");
  const FileIcon = isVideo ? FaVideo : FaImage;

  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <FileIcon className="h-5 w-5 shrink-0 text-gray-400" />

      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center justify-between gap-2">
          <p className="truncate text-sm font-medium text-gray-800">
            {item.file.name}
          </p>
          <div className="flex items-center gap-2">
            <FileUploadIcon status={item.status} />
            {item.status === "error" ? (
              <div className="flex items-center justify-between gap-2">
                <Button
                  variant="outline"
                  size="none"
                  width="none"
                  onClick={() => onRetry(item.id)}
                  className="px-2 py-1 text-xs"
                >
                  Retry
                </Button>
              </div>
            ) : null}
          </div>
        </div>

        {item.status === "uploading" ? (
          <div className="h-2 w-full rounded-full bg-gray-200">
            <div
              className="h-2 rounded-full bg-blue-500 transition-all duration-300"
              style={{ width: `${item.progress}%` }}
            />
          </div>
        ) : null}

        {item.status === "pending" ? (
          <p className="text-xs text-gray-500">Waiting to upload...</p>
        ) : null}
      </div>
    </div>
  );
}

function tryParseJson(maybeJson: string): unknown | undefined {
  try {
    return JSON.parse(maybeJson);
  } catch (_err) {
    return undefined;
  }
}

async function uploadFileToS3({
  url,
  method,
  body,
  onProgress,
}: {
  url: string;
  method: "POST" | "PUT";
  body: FormData | File;
  onProgress: FileUploadHandlerProps["onProgress"];
}): Promise<unknown | undefined> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        const percentComplete = (event.loaded / event.total) * 100;
        onProgress(percentComplete);
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(tryParseJson(xhr.responseText));
      } else {
        reject(new Error("Upload failed"));
      }
    });

    xhr.addEventListener("error", () => {
      reject(new Error("Network error during upload"));
    });

    xhr.addEventListener("abort", () => {
      reject(new Error("Upload was cancelled"));
    });

    xhr.open(method, url);

    // FYI, don't set the Content-Type header or it will probably break the
    // request for Pipe.

    xhr.send(body);
  });
}

type FileUploadHandlerProps = {
  file: File;
  onProgress: (progress: number) => void;
};

async function uploadPhoto({
  file,
  onProgress,
  cardId,
  sortOrder,
  durationSeconds,
  accessToken,
}: FileUploadHandlerProps & {
  cardId: string;
  sortOrder: number;
  durationSeconds: number;
  accessToken: string;
}): Promise<void> {
  const url = await s3Upload(file, accessToken, undefined, onProgress);

  const { response } = await createPhotoSegment(
    { cardId },
    { url, sortOrder, durationSeconds },
    async () => accessToken
  );

  if (!response.ok) {
    throw new Error("Something went wrong and this photo could not be saved.");
    return;
  }
}

async function uploadVideo({
  file,
  onProgress,
  payload,
  geoRegion,
}: FileUploadHandlerProps & {
  payload: PipeCustomPayload;
  geoRegion: GeoRegion;
}): Promise<{ videoId: string | undefined }> {
  const apiUrl =
    geoRegion === "us-west"
      ? "https://s3b-us.addpipe.com/upload"
      : "https://s3b-us2.addpipe.com/upload";

  const formData = new FormData();

  // This is the request PipeSDK makes to upload a video. This API is probably
  // not technically "public" in the sense that Pipe supports calling it outside
  // of PipeSDK. But we don't have another option.
  formData.append("accountHash", config.pipe.ACCOUNT_HASH);
  formData.append("payload", JSON.stringify(payload));
  formData.append("httpReferer", window.location.href);
  formData.append("environmentId", config.pipe.EID);
  formData.append("FileInput", file);

  // The format of the PipeSDK response looks like this:
  // "{\"s\":1,\"f\":\"300030d1b93c5f712cef7eac184db71f.mov\",\"id\":\"21397040\"}"
  // with an optional `e` field for an error message. The fields are:
  // - s: success status (1 for success, 0 for failure)
  // - f: file?
  // - d: description?
  // - id: A video identifier. We call this pipeId or videoId in various places.
  // - e: error message
  const response = await uploadFileToS3({
    url: apiUrl,
    method: "POST",
    body: formData,
    onProgress,
  });

  if (
    !response ||
    typeof response !== "object" ||
    !("s" in response) ||
    !("id" in response) ||
    !response.id ||
    response.s !== 1
  ) {
    throw new Error("Video upload failed.");
  }

  // I'm pretty sure id is always present, but since we're only using it to
  // add-pending, there's no reason to fail if it's not present.
  const videoId =
    "id" in response && response.id ? String(response.id) : undefined;

  return { videoId: videoId };
}

function getSupportedMediaText(
  supportedMedia: SupportedMedia,
  text: { video: string; photo: string; both: string }
) {
  if (supportedMedia.includes("video") && supportedMedia.includes("photo")) {
    return text.both;
  } else if (supportedMedia.includes("video")) {
    return text.video;
  } else if (supportedMedia.includes("photo")) {
    return text.photo;
  } else {
    return "";
  }
}

const getFileKey = (file: File) =>
  `${file.name}-${file.size}-${file.lastModified}`;

type SupportedMedia = Array<"video" | "photo">;

type UploadSegmentsModalProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  pipeUploadInfo: GetPipeVideoUploadPayloadProps;
  card: Card;
  /** Sort order for new uploaded clip (only applies to photo clips). */
  uploadedClipSortOrder: number;
  supportedMedia?: SupportedMedia;
  /** Called when one or more files are uploaded successfully. This really just
   * lets the parent reload segments. This will be debounced. */
  onUploadBatchSuccess?: () => void;
};

type UpdateUploadFn = (id: string, updates: Partial<FileUploadItem>) => void;

// We are defining this out here instead of in the component so that we can wrap
// it in nConcurrent and limit the number of concurrent uploads.
async function uploadFileManaged(
  id: string,
  file: File,
  {
    updateUpload,
    supportedMedia,
    pipeUploadInfo,
    accessToken,
    getAccessToken,
    geoRegion,
    card,
    uploadedClipSortOrder,
    onUploadSuccess,
    mediaType,
  }: {
    updateUpload: UpdateUploadFn;
    supportedMedia: SupportedMedia;
    pipeUploadInfo: GetPipeVideoUploadPayloadProps;
    accessToken: string;
    getAccessToken: GetAccessTokenFn;
    geoRegion: GeoRegion;
    card: Card;
    uploadedClipSortOrder: number;
    /** Called after every single file successful upload. */
    onUploadSuccess?: () => void;
    mediaType: "video" | "photo";
  }
) {
  updateUpload(id, { status: "uploading" });

  try {
    const onProgress = (progress: number) => {
      updateUpload(id, { progress });
    };

    if (mediaType === "video") {
      if (!supportedMedia.includes("video")) {
        updateUpload(id, { status: "error", error: "Unsupported media type." });

        return;
      }

      const payload = getPipeVideoUploadPayload(pipeUploadInfo);

      const { videoId } = await uploadVideo({
        file,
        onProgress,
        payload,
        geoRegion,
      });

      if (videoId) {
        try {
          await addPendingSegment(
            getAccessToken,
            { cardId: card.cardId },
            { pipeId: videoId, type: "video" }
          );
        } catch {
          // Silently ignore errors
        }
      }
    } else if (mediaType === "photo") {
      if (!supportedMedia.includes("photo")) {
        updateUpload(id, { status: "error", error: "Unsupported media type." });

        return;
      }

      if (!accessToken) {
        toastError(
          "You do not have permission to upload files. Please try logging in again."
        );
        return;
      }

      try {
        await uploadPhoto({
          file,
          onProgress,
          cardId: card.cardId,
          accessToken,
          sortOrder: uploadedClipSortOrder,
          durationSeconds:
            card.defaultPhotoClipDurationSeconds ??
            defaultPhotoClipDurationSeconds,
        });
      } catch (_err) {
        updateUpload(id, {
          status: "error",
          error:
            "Something went wrong and this photo could not be saved. Please try again.",
        });

        return;
      }
    } else {
      assertNever(mediaType);
    }

    updateUpload(id, {
      status: "completed",
      progress: 100,
    });

    onUploadSuccess?.();
  } catch (_err) {
    updateUpload(id, {
      status: "error",
      error: "Upload failed. Please try again.",
    });
  }
}

/**
 * Only exporting for Storybook.
 */
export function UploadSegmentsModalContent({
  onFilesSelected,
  supportedMedia,
  uploads,
  isUploading,
  onClose,
  onRetryUpload,
}: {
  onFilesSelected: (files: File[]) => void;
  supportedMedia: SupportedMedia;
  uploads: Map<string, FileUploadItem>;
  isUploading: boolean;
  onClose: () => void;
  onRetryUpload: (id: string) => void;
}) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sortedUploads = useMemo(
    () =>
      Array.from(uploads.values()).sort((a, b) => {
        const statusOrder = {
          ["uploading"]: 0,
          ["pending"]: 1,
          ["error"]: 2,
          ["completed"]: 3,
        };

        return statusOrder[a.status] - statusOrder[b.status];
      }),
    [uploads]
  );

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    // This turns the cursor into a plus sign (or something like that, depending
    // on the browser/OS)
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // We need to set this to true because it could have been set to false by
    // hovering a child element. Since we don't enter the drop zone again,
    // dragEnter won't fire again, so we need to set it to true here.
    setIsDragging(true);
    // This turns the cursor into a plus sign (or something like that, depending
    // on the browser/OS)
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);

    onFilesSelected(files);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);

      onFilesSelected(files);
    }
  };

  const handleFileChooseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <section className="w-full max-w-md space-y-4">
      <DialogTitle
        as="header"
        className="font-futura text-3xl leading-none font-medium text-gray-800 antialiased"
      >
        {getSupportedMediaText(supportedMedia, {
          video: "Add Videos",
          photo: "Add Photos",
          both: "Add Videos & Photos",
        })}
      </DialogTitle>

      <Description className="leading-tight font-medium text-gray-800 antialiased">
        Drag and drop your{" "}
        {getSupportedMediaText(supportedMedia, {
          video: "videos",
          photo: "photos",
          both: "videos & photos",
        })}{" "}
        or click to browse.
      </Description>

      <button
        className={cn(
          "mx-auto flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-20 py-10 transition-colors",
          isDragging
            ? "border-red-500 bg-red-50"
            : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100"
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleFileChooseClick}
      >
        <FaPhotoVideo
          className={cn(
            "h-20 w-20 transition-colors",
            isDragging ? "text-red-500" : "text-gray-300"
          )}
        />
        <p className="text-center text-gray-400">Drag & drop here</p>
      </button>

      <div className="flex justify-center">
        <Button
          onClick={handleFileChooseClick}
          variant="primary"
          className="flex items-center gap-2"
        >
          <FiUpload className="h-4 w-4" />
          Select Videos & Photos
        </Button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        multiple
        accept={[
          supportedMedia.includes("video") ? "video/*" : undefined,
          supportedMedia.includes("photo") ? "image/*" : undefined,
        ]
          .map(notEmpty)
          .join(",")}
        onChange={handleFileInputChange}
      />

      {supportedMedia.includes("video") ? (
        <p className="text-center text-sm text-gray-400">
          Note that videos may take a few minutes to process before they are
          available.
        </p>
      ) : null}

      {/* gray-500 rather than the gray-400 above it: gray-400 on white falls
          under WCAG AA. */}
      <ApplePhotosTip
        media={getSupportedMediaText(supportedMedia, {
          video: "videos",
          photo: "photos",
          both: "videos & photos",
        })}
        className="text-center text-sm text-gray-500"
      />

      {sortedUploads.length > 0 ? (
        <div className="max-h-96 space-y-2 overflow-y-auto">
          <h3 className="text-sm font-medium text-gray-700">
            Files ({sortedUploads.length})
          </h3>
          {sortedUploads.map((item) => (
            <FileUploadItem key={item.id} item={item} onRetry={onRetryUpload} />
          ))}
        </div>
      ) : null}

      <footer className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => onClose()}
          disabled={isUploading}
        >
          {isUploading ? "Uploading..." : "Close"}
        </Button>
      </footer>
    </section>
  );
}

const uploadFileManagedLimited = nConcurrent(
  MAX_CONCURRENT_UPLOADS,
  uploadFileManaged
);

export function UploadSegmentsModal({
  isOpen,
  setIsOpen,
  pipeUploadInfo,
  card,
  uploadedClipSortOrder,
  supportedMedia = ["video", "photo"],
  onUploadBatchSuccess,
}: UploadSegmentsModalProps) {
  const { getAccessToken } = useAuth();
  const { featureConfig } = useFeatureFlags();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const [uploads, setUploads] = useState<Map<string, FileUploadItem>>(
    new Map()
  );

  const onUploadBatchSuccessDebounced = useMemo(
    () =>
      onUploadBatchSuccess
        ? debounceTrailing(onUploadBatchSuccess, 3000)
        : undefined,
    [onUploadBatchSuccess]
  );

  const isUploading = useMemo(
    () =>
      Array.from(uploads.values()).some(
        (item) => item.status === "uploading" || item.status === "pending"
      ),
    [uploads]
  );

  const updateUpload = (id: string, updates: Partial<FileUploadItem>) => {
    setUploads((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(id);

      if (existing) {
        newMap.set(id, { ...existing, ...updates });
      }

      return newMap;
    });
  };

  const uploadFile = async (id: string, file: File) => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
      toastError(
        "You do not have permission to upload files. Please try logging in again."
      );
      return;
    }

    const mediaType = file.type.startsWith("video/") ? "video" : "photo";

    function handleUploadSuccess() {
      const uploaderType = "organizer";

      if (mediaType === "video") {
        sendAnalyticsEvent({
          event: "VideoClipSubmitted",
          data: {
            cardId: card.cardId,
            videoProvider: "pipe",
            contributorId: undefined,
            uploaderType,
          },
        });
      } else if (mediaType === "photo") {
        sendAnalyticsEvent({
          event: "PhotoClipSubmitted",
          data: {
            cardId: card.cardId,
            contributorId: undefined,
            uploaderType,
          },
        });
      } else {
        assertNever(mediaType);
      }

      onUploadBatchSuccessDebounced?.();
    }

    await uploadFileManagedLimited(id, file, {
      updateUpload,
      supportedMedia,
      pipeUploadInfo,
      accessToken,
      getAccessToken,
      geoRegion: featureConfig.region.geographic,
      card,
      uploadedClipSortOrder,
      mediaType,
      onUploadSuccess: handleUploadSuccess,
    });
  };

  const addFilesToQueue = (files: File[]) => {
    const newUploads = new Map<string, FileUploadItem>();

    files.forEach((file) => {
      const fileKey = getFileKey(file);

      // Check if file already exists in uploads
      const existingUpload = Array.from(uploads.entries()).find(
        ([_, item]) => getFileKey(item.file) === fileKey
      );

      if (existingUpload) {
        const [existingId, existingItem] = existingUpload;

        // If it's an error, retry it
        if (existingItem.status === "error") {
          retryUpload(existingId);
        }
        // Otherwise skip
        else {
          return;
        }
      }

      // New file - add to queue
      const id = `${file.name}-${Date.now()}-${Math.random()}`;
      newUploads.set(id, { id, file, status: "pending", progress: 0 });
    });

    // Merge new uploads with existing uploads
    setUploads((prev) => new Map([...prev, ...newUploads]));

    newUploads.forEach((item, id) => {
      uploadFile(id, item.file);
    });
  };

  const retryUpload = (id: string) => {
    const item = uploads.get(id);

    if (!item) {
      return;
    }

    updateUpload(id, {
      status: "pending",
      progress: 0,
      error: undefined,
    });

    uploadFile(id, item.file);
  };

  const processFiles = (files: File[]) => {
    const validFiles = files
      .filter(
        (file) =>
          (file.type.startsWith("image/") &&
            supportedMedia.includes("photo")) ||
          (file.type.startsWith("video/") && supportedMedia.includes("video"))
      )
      .filter((file) => {
        if (file.size > MAX_FILE_SIZE_BYTES) {
          toastError(
            `"${file.name}" exceeds the ${MAX_FILE_SIZE_HUMAN} size limit and cannot be uploaded.`
          );

          return false;
        } else {
          return true;
        }
      });

    if (validFiles.length > 0) {
      addFilesToQueue(validFiles);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      dismissable
      closeOnOverlayClick={!isUploading}
    >
      <UploadSegmentsModalContent
        onFilesSelected={processFiles}
        supportedMedia={supportedMedia}
        uploads={uploads}
        isUploading={isUploading}
        onClose={() => setIsOpen(false)}
        onRetryUpload={retryUpload}
      />
    </Modal>
  );
}
